import { Box, useMediaQuery, Button } from '@mui/material';
import { FC, useContext, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { Loader, MobileTable, Pagination, Table } from '../../../../../components';
import { getContactHistory, getEmailPreviewDetails } from '../../../../../fetch';
import {
  SALES_PORTAL_PER_PAGE_OPTIONS,
  formatEllipsis,
  formatShortFriendlyDateWithTime,
  getEnvironment,
  isTextOverflowing
} from '../../../../../helpers';
import { ISortDirection, IResponse, IClientHistory, IEmailDetails } from '../../../../../models';
import { TablePopover } from '../../../../../components/table/TablePopover';
import { ViewSentEmail } from './view-sent-email-modal';
import { SalesContext, update } from '../../../../../context/sales';

interface IClientHistoryList {
  buildId: string;
  emailSentRef: number;
}
const PAGE_NAME = 'Client-History-List';
export const ClientHistoryList: FC<IClientHistoryList> = ({ buildId, emailSentRef }) => {
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);
  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPage = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );
  const [selectedSort, setSelectedSort] = useState<string>('sentDate');
  const [isShowingViewSentEmail, setIsShowingViewSentEmail] = useState<boolean>(false);
  const [isLoadingEmailPreview, setIsLoadingEmailPreview] = useState<boolean>(false);
  const [emailPreviewDetails, setEmailPreviewDetails] = useState<IEmailDetails>();

  const [sortDirection, setSortDirection] = useState<{
    subject?: ISortDirection;
    sentDate?: ISortDirection;
  }>({
    sentDate: 'Asc'
  });

  const { isLoading, data: clientHistoryData } = useQuery<IResponse<IClientHistory[]>, Error>(
    ['getClientHistory', page, perPage, selectedSort, sortDirection, emailSentRef],
    async () =>
      await getContactHistory(buildId, {
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const fetchEmailPreview = async (id: number) => {
    try {
      setIsLoadingEmailPreview(true);
      const res = await getEmailPreviewDetails(id);
      setEmailPreviewDetails(res);
    } catch (error) {
    } finally {
      setIsLoadingEmailPreview(false);
    }
  };

  const handleOpenModal = (id: number) => {
    fetchEmailPreview(id);
    setIsShowingViewSentEmail(true);
  };

  const data = useMemo(() => clientHistoryData?.records ?? [], [clientHistoryData]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      {
        Header: 'Subject Line',
        accessor: 'subject',
        isServerSorted: selectedSort === 'subject',
        isServerSortedDesc: sortDirection.subject === 'Desc',
        handleClickColumn: () => handleClickColumn('subject')
      },
      {
        Header: 'Sent Date',
        accessor: 'sentDate',
        isServerSorted: selectedSort === 'sentDate',
        isServerSortedDesc: sortDirection.sentDate === 'Desc',
        handleClickColumn: () => handleClickColumn('sentDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IClientHistory } };
        }) => {
          return (
            <span>{`${
              formatShortFriendlyDateWithTime(original?.sentDate) ??
              'Email has been queued to send.'
            }`}</span>
          );
        }
      },
      {
        Header: 'Recipients',
        accessor: 'recipients',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IClientHistory } };
        }) => {
          return <Recipients recipients={original.recipientDisplay} />;
        }
      },
      {
        Header: 'Contact Type',
        accessor: 'contactType',
        isServerSorted: selectedSort === 'contactType',
        isServerSortedDesc: sortDirection.subject === 'Desc',
        handleClickColumn: () => handleClickColumn('contactType')
      },
      {
        Header: '',
        accessor: 'contactHistoryId',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IClientHistory } };
        }) => {
          return <Button onClick={() => handleOpenModal(original?.contactHistoryId)}>View</Button>;
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort]);

  return (
    <>
      {isLoading && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoading && (
        <Table
          stickyHeaders
          columns={columns}
          data={data}
          isLoading={isLoading}
          hidePagination
          mobileProps={{
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              })),
            handleView: original => handleOpenModal(original?.contactHistoryId),
            mobileDefaultAccessor: 'subject'
          }}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
        />
      )}
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
          setRowsPerPage={itemsPerPage => {
            return dispatchPerPage(
              update({
                pageName: PAGE_NAME,
                environment: getEnvironment(),
                itemsPerPage: itemsPerPage
              })
            );
          }}
        />
      )}
      <ViewSentEmail
        open={isShowingViewSentEmail}
        onClose={() => {
          setIsShowingViewSentEmail(false);
        }}
        emailPreviewDetails={emailPreviewDetails}
        isLoadingEmailPreview={isLoadingEmailPreview}
      />
    </>
  );
};

interface IRecipients {
  recipients: string;
}

const Recipients: FC<IRecipients> = ({ recipients }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onPopperOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const onPopperClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <TablePopover anchorEl={anchorEl} text={recipients} onPopperClose={onPopperClose} />
      <div
        aria-owns={!!anchorEl ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={e => {
          if (isTextOverflowing(recipients)) {
            onPopperOpen(e);
          }
        }}
        onMouseLeave={onPopperClose}
      >{`${formatEllipsis(recipients) ?? ''}`}</div>
    </>
  );
};
